import React from 'react';

import { Checkbox, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import { SALESFORCE_FIELD_MAX_LENGTH, audMoney, exceedsFieldLength } from '../constants';

import ImportNameField from './ImportNameField';
import type { ImportNameEdits } from './excelImportModel';
import type { ParsedInclusion } from './types';

type ImportInclusionsStepProps = {
  inclusions: Array<ParsedInclusion>;
  leftOutRows: ReadonlySet<number>;
  nameEdits: ImportNameEdits;
  onNameChange: (key: string, name: string) => void;
  onToggle: (row: number, included: boolean) => void;
  reusesAsset: (inclusionName: string) => boolean;
  rrpNotLoaded: string | null;
  withRrp: boolean;
};

export default function ImportInclusionsStep({
  inclusions,
  leftOutRows,
  nameEdits,
  onNameChange,
  onToggle,
  reusesAsset,
  rrpNotLoaded,
  withRrp,
}: ImportInclusionsStepProps) {
  const missingRrp = !withRrp || inclusions.some(({ rrp }) => rrp === null);
  return (
    <>
      <Typography color="text.secondary" variant="body2">
        {`Untick inclusions that don't belong to the base packages. The rest load onto every Option 1 package: narrow them, and fill in Type${missingRrp ? ' and RRP' : ''}, on Inclusions afterwards.${rrpNotLoaded ? ` ${rrpNotLoaded}` : ''}`}
      </Typography>
      <Table size="small" sx={{ mt: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell>Inclusion</TableCell>
            <TableCell>PRV</TableCell>
            <TableCell>RRP</TableCell>
            <TableCell>Asset</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {inclusions.map(({ row, name, prv, rrp }) => (
            <TableRow key={row}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={!leftOutRows.has(row)}
                  inputProps={{ 'aria-label': name }}
                  onChange={(event) => onToggle(row, event.target.checked)}
                />
              </TableCell>
              <TableCell sx={{ minWidth: 280 }}>
                {exceedsFieldLength(name, SALESFORCE_FIELD_MAX_LENGTH.inclusionName) ? (
                  <ImportNameField
                    disabled={leftOutRows.has(row)}
                    inputProps={{ 'aria-label': `Shorter name for ${name}` }}
                    kind="inclusion"
                    nameEdits={nameEdits}
                    onNameChange={onNameChange}
                    original={name}
                  />
                ) : (
                  name
                )}
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{prv === null ? '' : audMoney(prv)}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{withRrp && rrp !== null ? audMoney(rrp) : ''}</TableCell>
              <TableCell>{reusesAsset(name) ? 'Existing asset' : 'New asset'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}
